import React from "react";
import styled from "styled-components";
import ScannerErrorComponent from "../../components/error-component/ScannerErrorComponent";
import SearchBarComponent from "../../components/search-component/SearchBarComponent";

const OverlayArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
`;

const OverlayBlock = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.34);
`;

const HOLE_WIDTH_PCT = 0.9; // 90% of width
const HOLE_HEIGHT_PCT = 0.5; // 40% of height
const MAX_HOLE_WIDTH = 500; // Maximum width in pixels

const calculateHorizontalPadding = () => {
  const minPadding = ((1 - HOLE_WIDTH_PCT) / 2) * 100;
  return `max(${minPadding}%, calc((100% - ${MAX_HOLE_WIDTH}px) / 2))`;
};

const verticalPadding = `${((1 - HOLE_HEIGHT_PCT) / 2) * 100}%`;
const horizontalPadding = calculateHorizontalPadding();

interface OverlayBlockProps {
  verticalPadding: string;
  horizontalPadding: string;
}

const OverlayBlockTop = styled(OverlayBlock)<OverlayBlockProps>`
  top: 0;
  left: 0;
  right: 0;
  height: ${({ verticalPadding }) => verticalPadding};
`;

const OverlayBlockBottom = styled(OverlayBlock)<OverlayBlockProps>`
  bottom: 0;
  left: 0;
  right: 0;
  height: ${({ verticalPadding }) => verticalPadding};
`;

const OverlayBlockLeft = styled(OverlayBlock)<OverlayBlockProps>`
  top: ${({ verticalPadding }) => verticalPadding};
  bottom: ${({ verticalPadding }) => verticalPadding};
  left: 0;
  width: ${({ horizontalPadding }) => horizontalPadding};
`;

const OverlayBlockRight = styled(OverlayBlock)<OverlayBlockProps>`
  top: ${({ verticalPadding }) => verticalPadding};
  bottom: ${({ verticalPadding }) => verticalPadding};
  right: 0;
  width: ${({ horizontalPadding }) => horizontalPadding};
`;

const ScannerCorner = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  border: 3px solid white;
  z-index: 3;
`;

const TopLeftCorner = styled(ScannerCorner)<OverlayBlockProps>`
  top: calc(${({ verticalPadding }) => verticalPadding} - 5px);
  left: calc(${({ horizontalPadding }) => horizontalPadding} - 5px);
  border-right: none;
  border-bottom: none;
`;

const TopRightCorner = styled(ScannerCorner)<OverlayBlockProps>`
  top: calc(${({ verticalPadding }) => verticalPadding} - 5px);
  right: calc(${({ horizontalPadding }) => horizontalPadding} - 5px);
  border-left: none;
  border-bottom: none;
`;

const BottomLeftCorner = styled(ScannerCorner)<OverlayBlockProps>`
  bottom: calc(${({ verticalPadding }) => verticalPadding} - 5px);
  left: calc(${({ horizontalPadding }) => horizontalPadding} - 5px);
  border-right: none;
  border-top: none;
`;

const BottomRightCorner = styled(ScannerCorner)<OverlayBlockProps>`
  bottom: calc(${({ verticalPadding }) => verticalPadding} - 5px);
  right: calc(${({ horizontalPadding }) => horizontalPadding} - 5px);
  border-left: none;
  border-top: none;
`;

const VideoWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: black;
    display: block;
  }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ScannerArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  position: relative;
  gap: 2rem;
`;

const SearchBarWrapper = styled.p`
  position: absolute;

  max-width: 1000px;
  top: 0.5rem;
  width: 100%;
  z-index: 3;
`;

interface CameraScannerPagePresentationProps {
  videoComponent: React.ReactNode;
  isRdt?: boolean;
  error: string | null;
  setError: (err: string | null) => void;
  handleSearchClick: () => void;
}

const CameraScannerPagePresentation: React.FC<
  CameraScannerPagePresentationProps
> = ({ videoComponent, error, setError, handleSearchClick }) => {
  return (
    <PageContainer>
      <ScannerErrorComponent error={error} setError={setError} />
      <ScannerArea>
        <SearchBarWrapper>
          <SearchBarComponent onSearchBarClick={handleSearchClick} />
        </SearchBarWrapper>
        <VideoWrapper>{videoComponent}</VideoWrapper>
        <OverlayArea>
          <OverlayBlockTop
            verticalPadding={verticalPadding}
            horizontalPadding={horizontalPadding}
          />
          <OverlayBlockBottom
            verticalPadding={verticalPadding}
            horizontalPadding={horizontalPadding}
          />
          <OverlayBlockLeft
            verticalPadding={verticalPadding}
            horizontalPadding={horizontalPadding}
          />
          <OverlayBlockRight
            verticalPadding={verticalPadding}
            horizontalPadding={horizontalPadding}
          />
          <TopLeftCorner
            verticalPadding={verticalPadding}
            horizontalPadding={horizontalPadding}
          />
          <TopRightCorner
            verticalPadding={verticalPadding}
            horizontalPadding={horizontalPadding}
          />
          <BottomLeftCorner
            verticalPadding={verticalPadding}
            horizontalPadding={horizontalPadding}
          />
          <BottomRightCorner
            verticalPadding={verticalPadding}
            horizontalPadding={horizontalPadding}
          />
        </OverlayArea>
      </ScannerArea>
    </PageContainer>
  );
};

export default CameraScannerPagePresentation;
